import { Text, type Container } from 'pixi.js';
import { gsap } from 'gsap';
import {
  continentMeta,
  countryByCode,
  hexToNumber,
  type BroadcastEvent,
  type BroadcastState,
  type BroadcastTick,
  type ContinentId,
  type PhaseId,
} from '@fbwl/shared';
import { DESIGN } from '../core/runtime.js';
import type { CameraRig } from '../core/camera.js';
import type { FilterStack } from '../fx/filters.js';
import type { ParticleEngine } from '../fx/particles.js';
import type { AudioEngine } from '../audio/engine.js';
import type { SpeechEngine } from '../audio/speech.js';
import type { Hud } from '../ui/hud.js';
import type { GalaxyBackground } from '../world/galaxy.js';
import { AnimatedEarth } from '../world/earth.js';
import { Arena } from '../world/arena.js';
import { DuelStage } from '../world/duel.js';
import { BracketLadder } from '../world/bracket-ladder.js';
import { RoyaleRing } from '../world/royale-ring.js';
import { FlagCard } from '../world/flag-card.js';

/**
 * Battle-royale arena geometry, in design pixels.
 *
 * Portrait puts the ring dead centre and makes it as wide as the frame allows -
 * it is the hero of a vertical stream. Landscape offsets it right to leave the
 * qualifier board room down the left.
 */
function arenaGeometry(): { x: number; y: number; radius: number } {
  // Sized to sit between the qualifier board above and the flags bar below,
  // in both orientations, with a margin either side. Portrait can afford a
  // much larger ring, which is the whole reason this format suits vertical.
  return DESIGN.portrait
    ? { x: DESIGN.width / 2, y: 1078, radius: 364 }
    : { x: 1200, y: 498, radius: 278 };
}

interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Where each scene's flag field lives, per orientation.
 *
 * Landscape spreads wide and short; portrait goes tall and narrow, and keeps
 * clear of the header stack at the top and the ticker at the bottom. Collected
 * here rather than inline so the two layouts can be compared at a glance.
 */
const LAYOUT = {
  get earth(): { x: number; y: number } {
    return DESIGN.portrait
      ? { x: DESIGN.width / 2, y: DESIGN.height * 0.42 }
      : { x: DESIGN.width / 2, y: DESIGN.height / 2 };
  },
  get continents(): Box {
    return DESIGN.portrait
      ? { x: 70, y: 1080, width: 940, height: 560 }
      : { x: 980, y: 250, width: 880, height: 560 };
  },
  /** Where the camera looks during a continent segment. */
  get continentFocus(): { x: number; y: number } {
    return DESIGN.portrait
      ? { x: DESIGN.width / 2, y: DESIGN.height * 0.5 }
      : { x: DESIGN.width * 0.42, y: DESIGN.height / 2 };
  },
  get qualification(): Box {
    return DESIGN.portrait
      ? { x: 50, y: 500, width: 980, height: 1130 }
      : { x: 150, y: 232, width: 1620, height: 570 };
  },
  get draw(): Box {
    return DESIGN.portrait
      ? { x: 80, y: 560, width: 920, height: 1010 }
      : { x: 300, y: 250, width: 1320, height: 560 };
  },
  get hallOfFame(): Box {
    return DESIGN.portrait
      ? { x: 80, y: 680, width: 920, height: 800 }
      : { x: 260, y: 300, width: 1400, height: 440 };
  },
  /** The box the bracket ladder is fitted into, or null when it has no home. */
  get ladder(): Box | null {
    // Portrait has no column of spare width for a five-round ladder; the
    // scorebug, the phase rail and the ticker carry the same information there.
    return DESIGN.portrait ? null : { x: 40, y: 150, width: 640, height: 840 };
  },
} as const;

/** Where a full-frame headline sits, and where it moves for the champion. */
const HEADLINE_Y = DESIGN.height * (DESIGN.portrait ? 0.3 : 0.42);
const CHAMPION_HEADLINE_Y = DESIGN.height * (DESIGN.portrait ? 0.2 : 0.16);

export interface DirectorDependencies {
  readonly world: Container;
  readonly foreground: Container;
  readonly camera: CameraRig;
  readonly filters: FilterStack;
  readonly particles: ParticleEngine;
  readonly audio: AudioEngine;
  readonly speech: SpeechEngine;
  readonly hud: Hud;
  readonly galaxy: GalaxyBackground;
}

const NEON = 0x38bdf8;
const GOLD = 0xffc94d;

/**
 * The show's director.
 *
 * Owns every "what should be on screen right now" decision. The engine says
 * what is true; the director decides how it looks, sounds and moves.
 *
 * Each phase gets an `enter` that composes the world out of the same four
 * building blocks - globe, arena grid, duel stage, bracket ladder - plus a
 * camera intent and an audio mood. Keeping every phase in one file is
 * deliberate: the transitions between phases are the hard part, and they are
 * only reviewable when they sit next to each other.
 */
export class SceneDirector {
  private readonly earth = new AnimatedEarth(300);
  private readonly arena = new Arena();
  private readonly duel = new DuelStage();
  private readonly ladder = new BracketLadder();
  private readonly ring = new RoyaleRing();
  private readonly arenaGeo = arenaGeometry();
  private readonly headline: Text;
  private readonly subhead: Text;

  private phase: PhaseId | null = null;
  private championCard: FlagCard | null = null;
  private lastMatchId: string | null = null;
  /** Cached from the last snapshot so match-end can resolve the winning side. */
  private matchSides: { home: string; away: string } | null = null;
  private fireworkTimer = 0;
  private emberTimer = 0;
  private heartbeatTimer = 0;
  private tension = 0;
  /** Knockouts arrive in bursts; without this the mix is a wall of clicks. */
  private knockoutSoundCooldown = 0;

  constructor(private readonly deps: DirectorDependencies) {
    this.earth.view.position.set(LAYOUT.earth.x, LAYOUT.earth.y);

    this.headline = bigText('', 96);
    this.headline.position.set(DESIGN.width / 2, HEADLINE_Y);
    this.headline.alpha = 0;

    this.subhead = bigText('', 34);
    this.subhead.position.set(DESIGN.width / 2, HEADLINE_Y + 88);
    this.subhead.alpha = 0;
    this.subhead.style.fill = 0x9bb0cc;

    this.ring.configure(this.arenaGeo.x, this.arenaGeo.y, this.arenaGeo.radius);
    this.ring.setVisible(false);

    // The ring goes under the arena so flags sit inside it, not behind it.
    deps.world.addChild(
      this.earth.view,
      this.ring.view,
      this.arena.view,
      this.duel.view,
      this.ladder.view,
    );
    deps.foreground.addChild(this.headline, this.subhead);
  }

  // ------------------------------------------------------------------ inputs

  applyState(state: BroadcastState): void {
    this.deps.hud.setState(state);
    this.ladder.render(state.bracket);

    if (state.phase.id !== this.phase) {
      this.enterPhase(state.phase.id, state);
      this.phase = state.phase.id;
    } else {
      this.syncRoster(state);
    }

    if (state.match && state.match.id !== this.lastMatchId) {
      this.startDuel(state);
    }
  }

  applyTick(tick: BroadcastTick): void {
    this.deps.hud.setTick(tick);

    if (tick.match && this.duel.isActive) {
      const total = tick.match.homeScore + tick.match.awayScore;
      const balance = total === 0 ? 0.5 : tick.match.homeScore / total;
      // Blend the score with live momentum so the beam moves between goals.
      const momentum = (tick.match.homeMomentum - tick.match.awayMomentum) * 0.18;
      this.duel.setBalance(balance * 0.82 + 0.09 + momentum);
    }

    // Tension climbs as a phase runs out; it drives the heartbeat and the mood.
    this.tension = tick.phaseProgress;
  }

  handleEvent(event: BroadcastEvent): void {
    switch (event.type) {
      case 'commentary':
        this.deps.hud.showSubtitle(event.text, event.intensity);
        if (event.speak) {
          this.deps.speech.say(event.text, event.intensity);
          this.deps.audio.duck(event.intensity === 'epic' ? 0.22 : 0.38, 2.4);
        }
        break;

      case 'continent:spotlight':
        this.showContinent(event.continent, event.countries);
        break;

      case 'qualification:wave':
        this.deps.hud.recordEliminations(event.eliminated.length);
        this.playElimination(event.eliminated);
        break;

      case 'draw:pick':
        this.deps.audio.play('reveal', 0.6);
        this.arena.cardFor(event.code)?.setHighlight(true, GOLD);
        break;

      case 'match:intro':
        this.deps.audio.play('whoosh');
        break;

      case 'match:strike':
        this.playStrike(event.side, event.critical);
        break;

      case 'match:end':
        this.deps.hud.recordMatchPlayed();
        this.playMatchEnd(event.winner, event.upset, event.round);
        break;

      case 'champion':
        this.crownChampion(event.code);
        break;

      case 'ticker':
        this.deps.hud.pushTicker(event.items);
        break;

      case 'countdown':
        this.showCountdown(event.secondsLeft);
        break;

      case 'royale:round_start':
        this.startRoyaleRound(event.flags, event.final);
        break;

      case 'royale:knockout':
        this.deps.hud.recordEliminations(event.codes.length);
        this.playRoyaleKnockout(event.codes, event.aliveCount);
        break;

      case 'royale:round_end':
        this.endRoyaleRound(event.winner, event.final);
        break;

      default:
        break;
    }
  }

  update(deltaSeconds: number): void {
    this.earth.update(deltaSeconds);
    this.duel.update(deltaSeconds);
    this.ring.update(deltaSeconds);
    this.arena.update(deltaSeconds);

    this.knockoutSoundCooldown = Math.max(0, this.knockoutSoundCooldown - deltaSeconds);

    this.emberTimer -= deltaSeconds;
    if (this.emberTimer <= 0) {
      this.emberTimer = 0.6;
      if (this.phase === 'champion' || this.phase === 'opening' || this.phase === 'hall_of_fame') {
        this.deps.particles.embers(3);
      }
    }

    if (this.phase === 'champion') {
      this.fireworkTimer -= deltaSeconds;
      if (this.fireworkTimer <= 0) {
        this.fireworkTimer = 0.55 + Math.random() * 0.7;
        this.launchFirework();
      }
    }

    // A slow pulse under the closing minutes of any elimination phase.
    if (
      this.tension > 0.72 &&
      (this.phase === 'qualification_1' || this.phase === 'qualification_2')
    ) {
      this.heartbeatTimer -= deltaSeconds;
      if (this.heartbeatTimer <= 0) {
        this.heartbeatTimer = 1.5;
        this.deps.audio.play('heartbeat', 0.6);
      }
    }
  }

  // ------------------------------------------------------------------ phases

  private enterPhase(phase: PhaseId, state: BroadcastState): void {
    const { camera, filters, audio, galaxy } = this.deps;

    this.clearHeadline();
    this.duel.teardown();
    this.lastMatchId = null;
    this.destroyChampionCard();
    filters.setGodRays(false);
    galaxy.setAccent(0x2f6fd0);

    // Leaving battle royale for anything else puts the arena back to a grid.
    if (!phase.startsWith('royale_')) {
      this.arena.setLayout('grid');
      this.ring.setVisible(false);
    }

    switch (phase) {
      case 'opening': {
        this.arena.clear();
        this.earth.view.visible = true;
        this.earth.setSpinSpeed(0.03);
        this.earth.setAtmosphereColor(NEON);
        this.ladder.setVisible(false);

        camera.reset(0.1);
        camera.setDrift(0.6);
        camera.frame(DESIGN.width / 2, DESIGN.height / 2, 1.35, 0.1);
        camera.frame(DESIGN.width / 2, DESIGN.height / 2, 0.92, 9, 'sine.inOut');
        camera.orbit(50, 0.11);

        filters.setGodRays(true, 0.5);
        filters.flashBloom(2.6, 1.8);
        audio.setMood('ambient');
        audio.play('reveal');

        this.showHeadline('FLAG BATTLE WORLD LIVE', `SEASON ${state.season.number}`);
        break;
      }

      case 'continents': {
        this.earth.view.visible = true;
        this.earth.setSpinSpeed(0.006);
        this.arena.setRegion(LAYOUT.continents);
        this.arena.setRoster([], false);
        this.ladder.setVisible(false);

        camera.stopOrbit();
        camera.frame(LAYOUT.continentFocus.x, LAYOUT.continentFocus.y, 1.05, 2.4);
        camera.setDrift(0.5);
        audio.setMood('ambient');
        break;
      }

      case 'qualification_1':
      case 'qualification_2': {
        this.earth.view.visible = false;
        this.ladder.setVisible(false);
        this.arena.setRegion(LAYOUT.qualification);
        this.arena.setRoster(state.alive);
        this.arena.spotlight(null);

        camera.stopOrbit();
        camera.reset(1.6);
        camera.setDrift(1);
        camera.frame(DESIGN.width / 2, DESIGN.height / 2 + 20, 1.02, 2.2);

        audio.setMood(phase === 'qualification_1' ? 'tension' : 'battle');
        audio.cheer(0.5);
        this.showHeadline(
          phase === 'qualification_1' ? 'GLOBAL QUALIFICATION' : 'FINAL QUALIFICATION',
          `${state.alive.length} NATIONS`,
          2.6,
        );
        break;
      }

      case 'draw': {
        this.earth.view.visible = false;
        this.arena.setRegion(LAYOUT.draw);
        this.arena.setRoster(state.alive);
        this.ladder.setVisible(false);

        camera.reset(1.4);
        camera.setDrift(0.4);
        audio.setMood('tension');
        this.showHeadline('DRAW CEREMONY', '32 NATIONS - 16 TIES', 3);
        break;
      }

      case 'round_of_32':
      case 'round_of_16':
      case 'quarter_finals':
      case 'semi_finals':
      case 'grand_final': {
        this.earth.view.visible = false;
        this.arena.clear();
        this.ladder.render(state.bracket);
        this.ladder.setVisible(this.fitLadder());

        camera.stopOrbit();
        camera.reset(1.2);
        camera.setDrift(phase === 'grand_final' ? 1.3 : 0.8);

        audio.setMood(phase === 'grand_final' || phase === 'semi_finals' ? 'epic' : 'battle');
        if (phase === 'grand_final') {
          filters.setGodRays(true, 0.32);
          audio.cheer(1);
          this.showHeadline('GRAND FINAL', 'ONE MATCH FOR THE WORLD', 3.4);
        }
        break;
      }

      case 'champion': {
        this.earth.view.visible = false;
        this.arena.clear();
        this.ladder.setVisible(false);

        camera.reset(0.6);
        camera.setDrift(0.7);
        camera.orbit(34, 0.16);

        filters.setGodRays(true, 0.6);
        filters.flashBloom(3.2, 2.2);
        audio.setMood('victory');
        audio.cheer(1);
        audio.play('fanfare');
        this.deps.particles.confetti(240);
        break;
      }

      case 'hall_of_fame': {
        this.earth.view.visible = true;
        this.earth.setSpinSpeed(0.02);
        this.earth.setAtmosphereColor(GOLD);
        this.arena.clear();
        this.ladder.setVisible(false);

        camera.reset(1.4);
        camera.frame(DESIGN.width / 2, DESIGN.height / 2, 1.1, 6, 'sine.inOut');
        camera.setDrift(0.5);
        audio.setMood('ambient');
        this.showHallOfFame(state);
        break;
      }

      case 'restart': {
        this.earth.view.visible = true;
        this.earth.setSpinSpeed(0.05);
        this.earth.setAtmosphereColor(NEON);
        this.arena.clear();
        this.ladder.setVisible(false);

        camera.reset(1.6);
        camera.frame(DESIGN.width / 2, DESIGN.height / 2, 0.75, 6, 'sine.inOut');
        camera.setDrift(0.8);
        audio.setMood('ambient');
        this.showHeadline('NEXT SEASON', 'RESETTING THE WORLD', 3);
        break;
      }

      // ---------------------------------------------------------- battle royale

      case 'royale_qualifying':
      case 'royale_final': {
        this.enterRoyaleArena(phase === 'royale_final');
        // Rounds are announced by their own event, so nothing is drawn here -
        // this only sets the stage the rounds will run on.
        if (state.royale && state.royale.alive.length > 0) {
          this.arena.setRoster(state.royale.alive);
        }
        break;
      }

      case 'royale_champion': {
        this.earth.view.visible = false;
        this.ladder.setVisible(false);
        this.ring.setVisible(false);
        this.arena.clear();

        camera.reset(0.6);
        camera.setDrift(0.7);
        camera.orbit(34, 0.16);

        filters.setGodRays(true, 0.6);
        filters.flashBloom(3.2, 2.2);
        audio.setMood('victory');
        audio.cheer(1);
        audio.play('fanfare');
        this.deps.particles.confetti(260);
        break;
      }

      case 'royale_intermission': {
        this.earth.view.visible = true;
        this.earth.setSpinSpeed(0.05);
        this.earth.setAtmosphereColor(NEON);
        this.arena.clear();
        this.ring.setVisible(false);
        this.ladder.setVisible(false);

        camera.reset(1.6);
        camera.frame(DESIGN.width / 2, DESIGN.height / 2, 0.8, 6, 'sine.inOut');
        camera.setDrift(0.8);
        audio.setMood('ambient');
        this.showHeadline('NEXT TOURNAMENT', '194 FLAGS RETURN', 4);
        break;
      }
    }
  }

  /**
   * Place the bracket ladder, and report whether it earned its place on screen.
   * False in portrait, where there is no column of spare width for it.
   */
  private fitLadder(): boolean {
    const box = LAYOUT.ladder;
    if (!box) return false;
    return this.ladder.fitInto(box.x, box.y, box.width, box.height);
  }

  // ----------------------------------------------------------- battle royale

  private enterRoyaleArena(final: boolean): void {
    const { camera, filters, audio } = this.deps;

    this.earth.view.visible = false;
    this.ladder.setVisible(false);
    this.duel.teardown();

    this.arena.setLayout('disc');
    this.arena.setRegion({
      x: this.arenaGeo.x - this.arenaGeo.radius,
      y: this.arenaGeo.y - this.arenaGeo.radius,
      width: this.arenaGeo.radius * 2,
      height: this.arenaGeo.radius * 2,
    });

    this.ring.setVisible(true);

    camera.stopOrbit();
    camera.frame(this.arenaGeo.x, this.arenaGeo.y, 1, 1.4);
    camera.setDrift(final ? 1.1 : 0.6);

    filters.setGodRays(final, 0.34);
    audio.setMood(final ? 'epic' : 'battle');
  }

  private startRoyaleRound(flags: string[], final: boolean): void {
    this.enterRoyaleArena(final);
    this.ring.reset();
    this.arena.setRoster(flags);

    this.deps.audio.play('whoosh');
    this.deps.camera.push(1.04, 0.5);
    gsap.delayedCall(0.55, () => this.deps.camera.push(1, 1.2));

    if (final) {
      this.showHeadline('LAST FLAG STANDING', `${flags.length} QUALIFIERS`, 3.2);
      this.deps.audio.cheer(0.9);
      this.deps.filters.flashBloom(2.4, 1.4);
    }
  }

  private playRoyaleKnockout(codes: readonly string[], aliveCount: number): void {
    this.arena.eliminate(codes, (x, y, card) => {
      this.deps.particles.burst(x, y, {
        count: aliveCount <= 8 ? 26 : 8,
        colors: [card.primary, card.accent, 0xffffff],
        speed: aliveCount <= 8 ? 380 : 210,
        life: 0.7,
        size: aliveCount <= 8 ? 16 : 10,
        gravity: 560,
      });
    });

    // The last few knockouts each get their own sound and shake; the opening
    // carnage gets one shared hit, otherwise it is noise.
    if (this.knockoutSoundCooldown <= 0) {
      this.knockoutSoundCooldown = aliveCount <= 10 ? 0.05 : 0.16;
      this.deps.audio.play('eliminate', aliveCount <= 10 ? 0.9 : 0.45);
    }

    if (aliveCount <= 6) {
      this.deps.camera.shake(10);
      this.deps.audio.cheer(0.35);
    } else if (codes.length >= 6) {
      this.deps.camera.shake(6);
    }
  }

  private endRoyaleRound(winner: string, final: boolean): void {
    const country = countryByCode(winner);
    this.ring.celebrate(country?.name ?? winner);
    this.arena.cardFor(winner)?.setHighlight(true, GOLD);

    this.deps.audio.play(final ? 'fanfare' : 'horn');
    this.deps.audio.cheer(final ? 1 : 0.7);
    this.deps.camera.shake(final ? 30 : 16);
    this.deps.filters.flashBloom(final ? 3 : 2.1, 1.1);
    this.deps.particles.burst(this.arenaGeo.x, this.arenaGeo.y, {
      count: final ? 120 : 54,
      colors: [GOLD, 0xffffff, NEON],
      speed: 640,
      life: 1.6,
      size: 19,
      gravity: 360,
    });

    if (final) this.deps.particles.confetti(220);
  }

  /** Keep the arena in step with the roster after a reconnect or an admin skip. */
  private syncRoster(state: BroadcastState): void {
    const phase = state.phase.id;
    const gridPhase =
      phase === 'qualification_1' || phase === 'qualification_2' || phase === 'draw';
    if (!gridPhase) return;
    if (this.arena.size === state.alive.length) return;
    this.arena.setRoster(state.alive);
  }

  // ------------------------------------------------------------------ beats

  private showContinent(continent: ContinentId, codes: string[]): void {
    const meta = continentMeta(continent);
    const color = hexToNumber(meta.color);

    this.deps.galaxy.setAccent(color);
    this.earth.setAtmosphereColor(color);
    this.earth.lookAt(meta.longitude, 2.6);
    this.deps.audio.play('whoosh');
    this.deps.audio.cheer(0.5);

    this.arena.setRoster(codes);
    this.arena.spotlight(null);
    this.showHeadline(meta.name.toUpperCase(), `${codes.length} NATIONS - ${meta.tagline}`, 4.5);

    this.deps.camera.frame(LAYOUT.continentFocus.x, LAYOUT.continentFocus.y, 1.02, 2.4);
    this.deps.particles.burst(LAYOUT.earth.x, LAYOUT.earth.y, {
      count: 46,
      colors: [color, 0xffffff],
      speed: 420,
      gravity: 0,
      life: 1.6,
      size: 18,
    });
  }

  private playElimination(codes: readonly string[]): void {
    if (codes.length === 0) return;

    this.arena.eliminate(codes, (x, y, card) => {
      this.deps.particles.burst(x, y, {
        count: 14,
        colors: [card.primary, card.accent, 0xffffff],
        speed: 240,
        life: 0.85,
        size: 12,
        gravity: 620,
      });
    });

    this.deps.audio.play('eliminate', Math.min(1, 0.4 + codes.length * 0.06));
    this.deps.camera.shake(Math.min(20, 4 + codes.length * 0.8));

    if (codes.length >= 6) {
      this.deps.filters.glitch(6 + codes.length * 0.4, 0.28);
      this.deps.audio.cheer(0.4);
    }
  }

  private startDuel(state: BroadcastState): void {
    const match = state.match;
    if (!match) return;

    this.lastMatchId = match.id;
    this.matchSides = { home: match.home.code, away: match.away.code };
    this.duel.setup(match.home.code, match.away.code, match.home.rating, match.away.rating);
    this.ladder.highlight(match.id);

    const centre = this.duel.centre;
    this.deps.camera.frame(centre.x, centre.y, 1.06, 1.4);
    this.deps.camera.setDrift(0.7);
    this.deps.particles.burst(centre.x, centre.y, {
      count: 30,
      colors: [NEON, GOLD, 0xffffff],
      speed: 520,
      gravity: 0,
      life: 1,
      size: 16,
    });
  }

  private playStrike(side: 'home' | 'away', critical: boolean): void {
    if (!this.duel.isActive) return;

    // Sparks fly off the side that was hit, not the side that struck.
    const anchor = this.duel.anchorFor(side === 'home' ? 'away' : 'home');

    this.duel.strike(side, critical);
    this.deps.audio.play(critical ? 'critical' : 'strike');

    this.deps.particles.burst(anchor.x, anchor.y, {
      count: critical ? 46 : 20,
      colors: critical ? [GOLD, 0xffffff, 0xff8a3d] : [NEON, 0xffffff],
      speed: critical ? 620 : 380,
      life: critical ? 1.3 : 0.8,
      size: critical ? 20 : 13,
      gravity: 520,
    });

    if (critical) {
      this.deps.camera.shake(26);
      this.deps.filters.shock(anchor.x, anchor.y, 42);
      this.deps.filters.flashBloom(2.4, 0.7);
      this.deps.camera.push(1.13, 0.24);
      gsap.delayedCall(0.3, () => this.deps.camera.push(1.06, 0.9));
      this.deps.audio.cheer(0.6);
    } else {
      this.deps.camera.shake(9);
    }
  }

  private playMatchEnd(winner: string, upset: boolean, round: PhaseId): void {
    if (!this.duel.isActive) return;

    const side: 'home' | 'away' = this.matchSides?.home === winner ? 'home' : 'away';

    this.duel.finish(side);
    this.ladder.highlight(null);

    const anchor = this.duel.anchorFor(side);
    this.deps.audio.play('horn');
    this.deps.audio.cheer(round === 'grand_final' ? 1 : upset ? 0.9 : 0.6);
    this.deps.camera.shake(round === 'grand_final' ? 34 : 18);
    this.deps.filters.flashBloom(round === 'grand_final' ? 3 : 2, 1.2);
    this.deps.particles.burst(anchor.x, anchor.y, {
      count: round === 'grand_final' ? 120 : 60,
      colors: [GOLD, 0xffffff, NEON],
      speed: 700,
      life: 1.8,
      size: 20,
      gravity: 380,
    });

    if (upset) {
      this.deps.filters.glitch(14, 0.45);
      this.showHeadline('UPSET', winner, 2.4);
    }
  }

  private crownChampion(code: string): void {
    this.destroyChampionCard();

    const card = new FlagCard(code, { width: 520, showCode: true, showName: true, glow: true });
    card.position.set(DESIGN.width / 2, DESIGN.height * (DESIGN.portrait ? 0.52 : 0.5));
    card.setHighlight(true, GOLD);
    this.deps.world.addChild(card);
    this.championCard = card;

    card.alpha = 0;
    card.scale.set(0.2);
    gsap.to(card, { alpha: 1, duration: 0.8, ease: 'power2.out' });
    gsap.to(card.scale, { x: 1, y: 1, duration: 1.3, ease: 'elastic.out(1, 0.55)' });

    const country = countryByCode(code);
    this.showHeadline('WORLD CHAMPION', (country?.name ?? code).toUpperCase(), 8);
    this.headline.y = CHAMPION_HEADLINE_Y;
    this.subhead.y = CHAMPION_HEADLINE_Y + 88;

    this.deps.particles.confetti(320);
    for (let i = 0; i < 6; i++) {
      gsap.delayedCall(i * 0.22, () => this.launchFirework());
    }
    this.deps.audio.play('boom');
  }

  private showHallOfFame(state: BroadcastState): void {
    const champions = state.champions.slice(0, 10);
    if (champions.length === 0) {
      this.showHeadline('HALL OF FAME', 'THE FIRST NAME IS STILL TO BE WRITTEN', 6);
      return;
    }

    this.arena.setRegion(LAYOUT.hallOfFame);
    this.arena.setRoster(champions.map((c) => c.code));
    this.showHeadline('HALL OF FAME', `${champions.length} CROWNED NATIONS`, 6);
  }

  private showCountdown(seconds: number): void {
    this.deps.audio.play('countdown', seconds <= 3 ? 1 : 0.6);
    this.headline.text = String(seconds);
    this.headline.style.fontSize = 190;
    this.headline.alpha = 1;
    this.headline.scale.set(1.4);
    this.subhead.alpha = 0;

    gsap.to(this.headline.scale, { x: 1, y: 1, duration: 0.5, ease: 'back.out(2)' });
    gsap.to(this.headline, { alpha: 0, duration: 0.45, delay: 0.5 });
  }

  private launchFirework(): void {
    const x = DESIGN.width * (0.15 + Math.random() * 0.7);
    const y = DESIGN.height * (0.12 + Math.random() * 0.35);
    const palette = [GOLD, NEON, 0xff5d73, 0x43e08a, 0xffffff];
    this.deps.particles.firework(x, y, palette[Math.floor(Math.random() * palette.length)] ?? GOLD);
    this.deps.audio.play('boom', 0.55);
    this.deps.camera.shake(7);
  }

  private showHeadline(title: string, subtitle: string, holdSeconds = 4): void {
    this.headline.text = title;
    this.headline.style.fontSize = title.length > 18 ? 74 : 96;
    this.subhead.text = subtitle;
    this.headline.y = HEADLINE_Y;
    this.subhead.y = HEADLINE_Y + 88;

    gsap.killTweensOf([this.headline, this.subhead]);
    gsap.fromTo(
      this.headline,
      { alpha: 0, y: this.headline.y + 40 },
      { alpha: 1, y: this.headline.y, duration: 0.8, ease: 'power3.out' },
    );
    gsap.fromTo(
      this.subhead,
      { alpha: 0, y: this.subhead.y + 30 },
      { alpha: 1, y: this.subhead.y, duration: 0.8, delay: 0.15, ease: 'power3.out' },
    );
    gsap.to([this.headline, this.subhead], {
      alpha: 0,
      duration: 0.9,
      delay: holdSeconds,
      ease: 'power2.in',
    });
  }

  private clearHeadline(): void {
    gsap.killTweensOf([this.headline, this.subhead]);
    this.headline.alpha = 0;
    this.subhead.alpha = 0;
  }

  private destroyChampionCard(): void {
    if (!this.championCard) return;
    gsap.killTweensOf(this.championCard);
    this.championCard.destroy({ children: true });
    this.championCard = null;
  }
}

function bigText(text: string, size: number): Text {
  const node = new Text({
    text,
    style: {
      fontFamily: 'Bahnschrift, "Segoe UI", system-ui, sans-serif',
      fontSize: size,
      fontWeight: '700',
      fill: 0xffffff,
      letterSpacing: size * 0.12,
      align: 'center',
      dropShadow: { color: 0x0a1220, alpha: 0.9, blur: 18, distance: 0, angle: 0 },
    },
  });
  node.anchor.set(0.5);
  return node;
}
